import React, { useState, useEffect } from 'react';
import api from '../services/api';

const SSOSettings = () => {
  const [settings, setSettings] = useState({
    sso_enabled: { value: true, description: '' },
    sso_auto_register: { value: true, description: '' },
    sso_default_role: { value: 'employee', description: '' },
    sso_allowed_domains: { value: '', description: '' },
    google_sso_enabled: { value: true, description: '' },
    microsoft_sso_enabled: { value: true, description: '' },
    sso_require_email_verification: { value: false, description: '' },
    sso_force_for_domain: { value: '', description: '' },
  });

  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadSettings();
    loadStatistics();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/auth/settings');
      // Merge API data with defaults to ensure all properties exist
      setSettings(prev => ({
        ...prev,
        ...response.data
      }));
      setLoading(false);
    } catch (err) {
      console.error('Error loading SSO settings:', err);
      setError('Failed to load SSO settings');
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/auth/statistics');
      setStatistics(response.data);
    } catch (err) {
      console.error('Error loading SSO statistics:', err);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: !prev[key].value
      }
    }));
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    try {
      const settingsToSave = {};
      Object.keys(settings).forEach(key => {
        settingsToSave[key] = settings[key].value;
      });

      await api.put('/auth/settings', { settings: settingsToSave });

      setMessage('SSO settings saved successfully!');
      setTimeout(() => setMessage(''), 3000);

      // Reload statistics
      loadStatistics();
    } catch (err) {
      console.error('Error saving SSO settings:', err);
      setError('Failed to save SSO settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-bg-secondary py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold theme-text-primary">SSO Settings</h1>
          <p className="mt-2 text-sm theme-text-secondary">
            Configure Single Sign-On settings for Google and Microsoft authentication
          </p>
        </div>

        {/* Messages */}
        {message && (
          <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            {message}
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        {statistics?.users && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="theme-bg-primary rounded-lg shadow p-6">
              <div className="text-sm font-medium theme-text-secondary">Total Users</div>
              <div className="mt-2 text-3xl font-bold theme-text-primary">
                {statistics.users.total_users || 0}
              </div>
            </div>

            <div className="theme-bg-primary rounded-lg shadow p-6">
              <div className="text-sm font-medium theme-text-secondary">SSO Users</div>
              <div className="mt-2 text-3xl font-bold text-blue-600">
                {statistics.users.total_sso_users || 0}
              </div>
              <div className="text-xs theme-text-secondary mt-1">
                {statistics.users.total_users > 0
                  ? Math.round((statistics.users.total_sso_users / statistics.users.total_users) * 100)
                  : 0}% of total
              </div>
            </div>

            <div className="theme-bg-primary rounded-lg shadow p-6">
              <div className="text-sm font-medium theme-text-secondary">Google Users</div>
              <div className="mt-2 text-3xl font-bold" style={{ color: '#4285F4' }}>
                {statistics.users.google_users || 0}
              </div>
            </div>

            <div className="theme-bg-primary rounded-lg shadow p-6">
              <div className="text-sm font-medium theme-text-secondary">Microsoft Users</div>
              <div className="mt-2 text-3xl font-bold" style={{ color: '#00A4EF' }}>
                {statistics.users.microsoft_users || 0}
              </div>
            </div>
          </div>
        )}

        {/* Settings Form */}
        <div className="theme-bg-primary rounded-lg shadow">
          <div className="p-6 space-y-6">
            {/* Global SSO Settings */}
            <div>
              <h2 className="text-lg font-medium theme-text-primary mb-4">Global Settings</h2>

              <div className="space-y-4">
                {/* Enable SSO */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="font-medium theme-text-primary">Enable SSO</label>
                    <p className="text-sm theme-text-secondary">{settings.sso_enabled?.description || 'Enable or disable SSO globally'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('sso_enabled')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      settings.sso_enabled.value ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        settings.sso_enabled.value ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Auto Register */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <label className="font-medium theme-text-primary">Auto-Register New Users</label>
                    <p className="text-sm theme-text-secondary">
                      Automatically create accounts for users who sign in via SSO for the first time
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('sso_auto_register')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      settings.sso_auto_register.value ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        settings.sso_auto_register.value ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Default Role */}
                <div>
                  <label className="block font-medium theme-text-primary mb-2">
                    Default Role for New SSO Users
                  </label>
                  <select
                    value={settings.sso_default_role.value}
                    onChange={(e) => handleInputChange('sso_default_role', e.target.value)}
                    className="form-input"
                  >
                    <option value="employee">Employee</option>
                    <option value="manager">Manager</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="hr_manager">HR Manager</option>
                    <option value="accountant">Accountant</option>
                  </select>
                </div>

                {/* Allowed Domains */}
                <div>
                  <label className="block font-medium theme-text-primary mb-2">
                    Allowed Email Domains
                  </label>
                  <input
                    type="text"
                    value={settings.sso_allowed_domains.value}
                    onChange={(e) => handleInputChange('sso_allowed_domains', e.target.value)}
                    placeholder="company.com, partner.com (leave empty for all domains)"
                    className="form-input"
                  />
                  <p className="mt-1 text-sm theme-text-secondary">
                    Comma-separated list of allowed email domains. Leave empty to allow all domains.
                  </p>
                </div>

                {/* Force SSO for Domains */}
                <div>
                  <label className="block font-medium theme-text-primary mb-2">
                    Force SSO for Domains
                  </label>
                  <input
                    type="text"
                    value={settings.sso_force_for_domain.value}
                    onChange={(e) => handleInputChange('sso_force_for_domain', e.target.value)}
                    placeholder="company.com (leave empty to allow password login)"
                    className="form-input"
                  />
                  <p className="mt-1 text-sm theme-text-secondary">
                    Users with these email domains MUST use SSO (password login disabled).
                  </p>
                </div>
              </div>
            </div>

            {/* Provider Settings */}
            <div className="border-t theme-border-primary pt-6">
              <h2 className="text-lg font-medium theme-text-primary mb-4">Provider Settings</h2>

              <div className="space-y-4">
                {/* Google SSO */}
                <div className="flex items-center justify-between p-4 border theme-border-primary rounded-lg">
                  <div className="flex items-center space-x-3">
                    <svg className="w-8 h-8" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <div>
                      <div className="font-medium theme-text-primary">Google SSO</div>
                      <div className="text-sm theme-text-secondary">
                        {statistics?.users?.google_users || 0} users
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('google_sso_enabled')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      settings.google_sso_enabled.value ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        settings.google_sso_enabled.value ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Microsoft SSO */}
                <div className="flex items-center justify-between p-4 border theme-border-primary rounded-lg">
                  <div className="flex items-center space-x-3">
                    <svg className="w-8 h-8" viewBox="0 0 23 23">
                      <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
                      <path fill="#f35325" d="M1 1h10v10H1z"/>
                      <path fill="#81bc06" d="M12 1h10v10H12z"/>
                      <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                      <path fill="#ffba08" d="M12 12h10v10H12z"/>
                    </svg>
                    <div>
                      <div className="font-medium theme-text-primary">Microsoft SSO</div>
                      <div className="text-sm theme-text-secondary">
                        {statistics?.users?.microsoft_users || 0} users
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleToggle('microsoft_sso_enabled')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      settings.microsoft_sso_enabled.value ? 'bg-primary-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        settings.microsoft_sso_enabled.value ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="border-t theme-border-primary pt-6">
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn-primary px-6 py-2"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>

        {/* Configuration Instructions */}
        <div className="mt-8 theme-bg-primary rounded-lg shadow p-6">
          <h2 className="text-lg font-medium theme-text-primary mb-4">Configuration Instructions</h2>

          <div className="space-y-4 text-sm theme-text-secondary">
            <div>
              <h3 className="font-medium theme-text-primary mb-2">Google OAuth Credentials</h3>
              <ol className="list-decimal list-inside space-y-1">
                <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">Google Cloud Console</a></li>
                <li>Create a project and enable Google Calendar API</li>
                <li>Configure OAuth consent screen</li>
                <li>Create OAuth 2.0 credentials (Web application)</li>
                <li>Add redirect URI: <code className="px-2 py-1 bg-gray-100 rounded">https://hr.thesplendidlife.com/auth/google/callback</code></li>
                <li>Copy Client ID and Secret to your .env file</li>
              </ol>
            </div>

            <div>
              <h3 className="font-medium theme-text-primary mb-2">Microsoft OAuth Credentials</h3>
              <ol className="list-decimal list-inside space-y-1">
                <li>Go to <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">Azure Portal</a></li>
                <li>Register an application in Azure Active Directory</li>
                <li>Configure API permissions (openid, email, profile, User.Read)</li>
                <li>Add redirect URI: <code className="px-2 py-1 bg-gray-100 rounded">https://hr.thesplendidlife.com/auth/microsoft/callback</code></li>
                <li>Create a client secret</li>
                <li>Copy Application ID and Secret to your .env file</li>
              </ol>
            </div>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800">
                <strong>Note:</strong> After updating OAuth credentials in your .env file, you must restart the backend server for changes to take effect.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SSOSettings;
